import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { useLocalStorage } from '../helpers/utility'
import { useMemo } from 'react'
import { useLocationPricing } from './useLocationPricing'

export const FEATURE_FLAGS = {
	WORD_COUNT: 'word-count-2',
	PHILIPPINES_PRICING: 'philippines-pricing',
	PADDLE: 'paddle',
	ASK_AI_PROMPT: 'ask-ai-prompt',
	MONTHLY_SUGGESTION: 'monthly-suggestion',
	CUSTOM_SOURCE: 'custom-source',
	HUMANIZER_V4: 'humanizer-v4',
	SENTENCE_HUMANIZER: 'sentence-humanizer',
	CUSTOM_REFERENCE_V2: 'custom-reference-v2',
	GRADER_V3: 'grader-v3',
	EDITOR_BAR: 'editor-bar',
	OUTLINER_UPSELL: 'outliner-upsell',
	SENTENCE_REFERENCE: 'sentence-reference',
	INCREASE_EDITOR_BAR_VISIBILITY: 'increase-editor-bar-visibility',
	SHORT_WRITER_V2: 'short-writer-v2',
	DEEMPHASIZE_CANCEL: 'deemphasize-cancel',
	SHOW_COMPLETE_BUTTON: 'show-complete-button',
	UPGRADE_SIDE_PANEL: 'upgrade-side-panel',
	DOCUMENTS_EMPTY_STATE: 'documents-empty-state',
	DEFAULT_PLAGIARISM_TAB: 'default-plagiarism-tab',
	UPDATED_LOADER: 'updated-loader',
	COMPLETE_TO_SIGNUP: 'complete-to-signup',
	WORD_COUNT_OPTIONS: 'word-count-options',
	FINALIZE_BUTTON: 'finalize-button',
	PERSONAS: 'personas',
	REMOVE_PER_MONTH: 'remove-per-month',
	SHOW_FULL_ANNUAL_PRICE: 'show-full-annual-price',
	NEW_PRICING_TIERS: 'new-pricing-tiers',
	NEW_PRICING_TIERS_V2: 'new-pricing-tiers-v2',
	MASK_HUMANIZER: 'mask-humanizer',
	MASK_IN_UPGRADE_MODAL: 'mask-in-upgrade-modal',
	NEW_UPGRADE_MODAL: 'new-upgrade-modal',
	LIMITED_TRIAL: 'limited-trial',
	UPGRADE_MODAL_UPDATES: 'upgrade-modal-updates',
	ASYNC_SHORT_ESSAY: 'async-short-essay',
}

export const usePricing = () => {
	const { pricing: locationPricing, locationData, isLoading: locationLoading } = useLocationPricing()

	// Check if new pricing tiers feature flag is enabled
	const newPricingTiersV2FF = useFeatureFlagVariantKey(FEATURE_FLAGS.NEW_PRICING_TIERS_V2)
	const limitedTrialFF = useFeatureFlagVariantKey(FEATURE_FLAGS.LIMITED_TRIAL)
	const isNewPricingEnabled = newPricingTiersV2FF === 'test' || limitedTrialFF === 'test'

	const defaultPricing = isNewPricingEnabled
		? {
				monthly: '35.99',
				yearly: '17.00',
				yearlyTotal: '199.00',
				discount: '50',
				currencySymbol: '$',
				flagEmoji: '🇺🇸',
		  }
		: {
				monthly: '9.00',
				yearly: '6.00',
				yearlyTotal: '72.00',
				discount: '33',
				currencySymbol: '$',
				flagEmoji: '🇺🇸',
		  }

	const pricing = useMemo(() => {
		// If location data is available, use location-based pricing
		if (locationData?.country_code && !locationLoading) {
			return {
				monthly: locationPricing.monthly,
				yearly: locationPricing.yearly,
				yearlyTotal: locationPricing.yearlyTotal,
				discount: locationPricing.discount,
				currencySymbol: locationPricing.currencySymbol,
				flagEmoji: locationPricing.flagEmoji,
			}
		}

		// Fallback to default pricing
		return defaultPricing
	}, [locationPricing, locationData, locationLoading, defaultPricing])

	return {
		...pricing,
		countryCode: locationData?.country_code || 'US',
		stripePriceId:
			locationData?.country_code && !locationLoading
				? locationPricing.stripePriceId
				: {
						yearly: 'price_1Ql4ogApflHFLqu6XPdDmIFI',
						monthly: 'price_1QnxlvApflHFLqu60NqtwX8v',
				  },
		locationData,
		isLocationLoading: locationLoading,
		isNewPricingEnabled,
	}
}

export const useFreeWordLimit = () => {
	const variant = useFeatureFlagVariantKey(FEATURE_FLAGS.WORD_COUNT)

	const variantToLimit = {
		control: 250,
		'150': 150,
		'350': 350,
	}

	// @ts-ignore
	return variantToLimit[variant ?? 'control'] as 150 | 250 | 350
}

export const useAnnualPlan = () => {
	const [annualPlan, setAnnualPlan] = useLocalStorage('annualPlan', false)

	return [annualPlan, setAnnualPlan] as const
}
